import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  HandCoins,
  Plus,
  Sparkles,
  X,
  Check,
  CalendarClock,
  Wallet,
  Trash2,
  BadgeCheck,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  UserPlus,
  Users,
} from 'lucide-react';
import api from '../lib/axios';
import { euro } from '../lib/format';
import { useIsTenantAdmin } from '../stores/authStore';

type ScheduleMode = 'FULL' | 'INSTALLMENTS' | 'MONTHLY';
type Source = 'MANUAL' | 'AUTO';
type ContributionStatus = 'ACTIVE' | 'COMPLETED' | 'PENDING';

interface InstallmentView {
  month: string;
  expected: number;
  paid: number;
  auto: boolean;
}

interface Contribution {
  id: number;
  contactId: number;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  mode: ScheduleMode;
  totalExpected: number;
  collected: number;
  installmentCount: number;
  startMonth: string | null;
  incomeAccount: string | null;
  matchLabel: string | null;
  source: Source;
  status: ContributionStatus;
  installments: InstallmentView[];
}

interface ContactOption {
  id: number;
  displayName: string;
  email: string | null;
}

interface Suggestion {
  suggestedName: string;
  firstName: string | null;
  lastName: string | null;
  matchLabel: string;
  monthlyAmount: number;
  monthsDetected: number;
  totalDetected: number;
  months: string[];
  incomeAccount: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9\s.\-()]{7,}$/;
const isValidEmail = (v: string) => EMAIL_RE.test(v.trim());
const isValidPhone = (v: string) => PHONE_RE.test(v.trim());

const MONTH_LABEL = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

type ContactMode = 'existing' | 'new';

type Form = {
  contactMode: ContactMode;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  mode: ScheduleMode;
  total: string;
  count: string;
  startMonth: string;
  incomeAccount: string;
  matchLabel: string;
};

const EMPTY_FORM: Form = {
  contactMode: 'existing',
  contactId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  mode: 'INSTALLMENTS',
  total: '',
  count: '10',
  startMonth: currentMonth(),
  incomeAccount: '756',
  matchLabel: '',
};

const MODES: ScheduleMode[] = ['FULL', 'INSTALLMENTS', 'MONTHLY'];

export default function Cotisants() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isTenantAdmin = useIsTenantAdmin();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Form>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);

  const { data: list = [] } = useQuery({
    queryKey: ['contributions'],
    queryFn: () => api.get<Contribution[]>('/contributions').then((r) => r.data),
  });

  const { data: suggestions = [] } = useQuery({
    queryKey: ['contribution-suggestions'],
    queryFn: () => api.get<Suggestion[]>('/contributions/suggestions').then((r) => r.data),
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'options'],
    queryFn: () => api.get<ContactOption[]>('/contacts').then((r) => r.data),
  });

  const { data: settings } = useQuery({
    queryKey: ['contribution-settings'],
    queryFn: () =>
      api.get<{ requireAutoValidation: boolean }>('/contributions/settings').then((r) => r.data),
  });

  const totals = useMemo(() => {
    const active = list.filter((c) => c.status !== 'PENDING');
    const expected = active.reduce((s, c) => s + c.totalExpected, 0);
    const collected = active.reduce((s, c) => s + c.collected, 0);
    return { expected, collected, count: active.length };
  }, [list]);

  const previewPer = useMemo(() => {
    const total = Number(form.total) || 0;
    if (form.mode === 'FULL') return total;
    const n = Math.max(1, Number(form.count) || 1);
    return Math.round((total / n) * 100) / 100;
  }, [form]);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.displayName.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q),
    );
  }, [contacts, contactSearch]);

  const detail = list.find((c) => c.id === detailId) ?? null;

  const resetForm = () => {
    setShowForm(false);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setContactSearch('');
  };

  const visibleSuggestions = useMemo(
    () => suggestions.filter((s) => !dismissed.includes(s.matchLabel)),
    [suggestions, dismissed],
  );

  const prefillFromSuggestion = (s: Suggestion) => {
    const parts = s.suggestedName.trim().split(/\s+/);
    const firstName = s.firstName ?? (parts.length > 1 ? parts[0] : '');
    const lastName =
      s.lastName ?? (parts.length > 1 ? parts.slice(1).join(' ') : s.suggestedName.trim());
    setForm({
      ...EMPTY_FORM,
      contactMode: 'new',
      firstName,
      lastName,
      mode: 'INSTALLMENTS',
      total: String(s.totalDetected),
      count: String(Math.max(1, s.monthsDetected)),
      startMonth: s.months[0] ?? currentMonth(),
      incomeAccount: s.incomeAccount ?? '756',
      matchLabel: s.matchLabel,
    });
    setFormError(null);
    setShowForm(true);
  };

  const create = useMutation({
    mutationFn: () => {
      const total = Number(form.total) || 0;
      const body =
        form.contactMode === 'existing'
          ? { contactId: Number(form.contactId) }
          : {
              firstName: form.firstName.trim() || null,
              lastName: form.lastName.trim() || null,
              email: form.email.trim() || null,
              phone: form.phone.trim() || null,
              address: form.address.trim() || null,
            };
      return api
        .post<Contribution>('/contributions', {
          ...body,
          mode: form.mode,
          totalAmount: total,
          installmentCount: form.mode === 'FULL' ? 1 : Math.max(1, Number(form.count) || 1),
          startMonth: form.startMonth,
          incomeAccount: form.incomeAccount.trim() || '756',
          matchLabel: form.matchLabel.trim() || null,
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contributions'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      resetForm();
    },
    onError: (e: any) => setFormError(e?.response?.data?.message ?? t('cotisants.saveError')),
  });

  const validate = useMutation({
    mutationFn: (id: number) =>
      api.post<Contribution>(`/contributions/${id}/validate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contributions'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/contributions/${id}`).then((r) => r.data),
    onSuccess: (_d, id) => {
      if (detailId === id) setDetailId(null);
      qc.invalidateQueries({ queryKey: ['contributions'] });
    },
  });

  const saveSettings = useMutation({
    mutationFn: (requireAutoValidation: boolean) =>
      api
        .put<{ requireAutoValidation: boolean }>('/contributions/settings', { requireAutoValidation })
        .then((r) => r.data),
    onSuccess: (data) => qc.setQueryData(['contribution-settings'], data),
  });

  const submit = () => {
    setFormError(null);
    const total = Number(form.total) || 0;
    if (form.contactMode === 'existing') {
      if (!form.contactId) {
        setFormError(t('cotisants.errorContact'));
        return;
      }
    } else {
      if (!form.firstName.trim() && !form.lastName.trim()) {
        setFormError(t('cotisants.errorName'));
        return;
      }
      if (form.email.trim() && !isValidEmail(form.email)) {
        setFormError(t('cotisants.errorEmail'));
        return;
      }
      if (form.phone.trim() && !isValidPhone(form.phone)) {
        setFormError(t('cotisants.errorPhone'));
        return;
      }
    }
    if (total <= 0) {
      setFormError(t('cotisants.errorAmount'));
      return;
    }
    create.mutate();
  };

  const modeLabel = (c: Contribution) => {
    if (c.mode === 'FULL') return t('cotisants.modeFull');
    if (c.mode === 'MONTHLY') return t('cotisants.modeMonthly');
    return t('cotisants.modeInstallmentsN', { count: c.installmentCount });
  };

  const paidCount = (c: Contribution) =>
    c.installments.filter((i) => i.paid >= i.expected && i.expected > 0).length;

  const name = (c: {
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
  }) => c.displayName ?? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <HandCoins size={14} /> {t('cotisants.eyebrow')}
          </p>
          <h1 className="page-title mt-1">{t('cotisants.title')}</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          <Plus size={16} /> {t('cotisants.add')}
        </button>
      </header>

      {/* KPI strip */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-xs text-ink-muted">{t('cotisants.kpiCount')}</p>
          <p className="mt-1 font-display text-2xl font-semibold text-ink">{totals.count}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-ink-muted">{t('cotisants.kpiExpected')}</p>
          <p className="mt-1 font-display text-2xl font-semibold text-ink">{euro(totals.expected)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-ink-muted">{t('cotisants.kpiCollected')}</p>
          <p className="mt-1 font-display text-2xl font-semibold text-income">{euro(totals.collected)}</p>
        </div>
      </div>

      {/* Auto-detected suggestions */}
      {visibleSuggestions.length > 0 && (
        <div className="card mb-6 p-4">
          <div className="mb-3 flex items-start gap-2">
            <Sparkles size={18} className="mt-0.5 shrink-0 text-brand" />
            <div>
              <p className="text-sm font-medium text-ink">{t('cotisants.autoTitle')}</p>
              <p className="text-xs text-ink-muted">{t('cotisants.autoSubtitle')}</p>
            </div>
          </div>
          <ul className="space-y-2">
            {visibleSuggestions.map((s) => (
              <li
                key={s.matchLabel}
                className="flex flex-wrap items-center gap-3 rounded-lg bg-surface-raised p-3"
              >
                <div className="min-w-[140px] flex-1">
                  <p className="font-medium text-ink">{s.suggestedName}</p>
                  <p className="text-xs text-ink-muted">
                    {t('cotisants.monthsDetected', { count: s.monthsDetected })} · {euro(s.monthlyAmount)}
                    /{t('cotisants.month').toLowerCase()}
                  </p>
                </div>
                <span className="font-medium text-income">{euro(s.totalDetected)}</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => prefillFromSuggestion(s)} className="btn btn-primary !py-1.5">
                    <Plus size={14} /> {t('cotisants.createFromSuggestion')}
                  </button>
                  <button
                    onClick={() => setDismissed((d) => [...d, s.matchLabel])}
                    className="btn btn-ghost !px-2 !py-1.5"
                    title={t('cotisants.ignore')}
                  >
                    <X size={15} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Admin setting: require validation for auto-detected contributors */}
      {isTenantAdmin && settings && (
        <div className="card mb-6 flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-start gap-2">
            <ShieldCheck size={18} className="mt-0.5 shrink-0 text-ink-muted" />
            <div>
              <p className="text-sm font-medium text-ink">{t('cotisants.requireValidationTitle')}</p>
              <p className="text-xs text-ink-muted">{t('cotisants.requireValidationHint')}</p>
            </div>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={settings.requireAutoValidation}
              onChange={(e) => saveSettings.mutate(e.target.checked)}
              className="h-4 w-4 accent-brand"
            />
            <span className="text-sm text-ink-soft">{t('cotisants.requireValidationToggle')}</span>
          </label>
        </div>
      )}

      {/* Contributors list */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('cotisants.name')}</th>
                <th>{t('cotisants.mode')}</th>
                <th>{t('cotisants.progress')}</th>
                <th className="text-right">{t('cotisants.amount')}</th>
                <th>{t('cotisants.source')}</th>
                <th aria-label={t('common.edit')} />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const ratio = c.totalExpected ? Math.min(1, c.collected / c.totalExpected) : 0;
                return (
                  <tr key={c.id} className="cursor-pointer" onClick={() => setDetailId(c.id)}>
                    <td data-label={t('cotisants.name')} className="font-medium text-ink">
                      {name(c)}
                    </td>
                    <td data-label={t('cotisants.mode')}>
                      <span className="pill pill-neutral">{modeLabel(c)}</span>
                    </td>
                    <td data-label={t('cotisants.progress')} className="min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-raised">
                          <div
                            className="h-full rounded-full bg-brand transition-all"
                            style={{ width: `${ratio * 100}%` }}
                          />
                        </div>
                        <span className="shrink-0 text-xs text-ink-muted">
                          {paidCount(c)}/{c.installmentCount}
                        </span>
                      </div>
                    </td>
                    <td data-label={t('cotisants.amount')} className="text-right">
                      <span className="font-medium text-income">{euro(c.collected)}</span>
                      <span className="text-ink-muted"> / {euro(c.totalExpected)}</span>
                    </td>
                    <td data-label={t('cotisants.source')}>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {c.source === 'AUTO' ? (
                          <span className="pill pill-posted" title={t('cotisants.aiExtractedHint')}>
                            <Sparkles size={11} /> {t('cotisants.sourceAuto')}
                          </span>
                        ) : (
                          <span className="pill pill-neutral">{t('cotisants.sourceManual')}</span>
                        )}
                        {c.status === 'PENDING' && (
                          <span className="pill pill-draft" title={t('cotisants.pendingHint')}>
                            <AlertTriangle size={11} /> {t('cotisants.pending')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === 'PENDING' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              validate.mutate(c.id);
                            }}
                            className="btn btn-primary !px-2 !py-1"
                            title={t('cotisants.validate')}
                          >
                            <BadgeCheck size={15} /> {t('cotisants.validate')}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            remove.mutate(c.id);
                          }}
                          className="btn btn-ghost !px-2 !py-1"
                          title={t('common.delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-ink-muted">
                    {t('cotisants.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add modal */}
      {showForm && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="reveal card mt-8 w-full max-w-lg p-6">
            <div className="mb-4 flex items-center justify-between">
              <p className="eyebrow flex items-center gap-2">
                <HandCoins size={14} /> {t('cotisants.add')}
              </p>
              <button onClick={resetForm} className="btn btn-secondary">
                <X size={16} /> {t('common.close')}
              </button>
            </div>

            <div className="space-y-3">
              {/* Contact: existing or new */}
              <div>
                <label className="field-label">{t('cotisants.contact')}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, contactMode: 'existing' }))}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      form.contactMode === 'existing'
                        ? 'border-brand bg-brand-tint text-brand-strong'
                        : 'border-line text-ink-soft hover:bg-surface-raised'
                    }`}
                  >
                    <Users size={16} /> {t('cotisants.contactExisting')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, contactMode: 'new' }))}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      form.contactMode === 'new'
                        ? 'border-brand bg-brand-tint text-brand-strong'
                        : 'border-line text-ink-soft hover:bg-surface-raised'
                    }`}
                  >
                    <UserPlus size={16} /> {t('cotisants.contactNew')}
                  </button>
                </div>
              </div>

              {form.contactMode === 'existing' ? (
                <div className="space-y-2">
                  <input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    className="input"
                    placeholder={t('cotisants.contactSearch')}
                  />
                  <select
                    value={form.contactId}
                    onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
                    className="input"
                    size={5}
                  >
                    {filteredContacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                        {c.email ? ` — ${c.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label">{t('cotisants.firstName')}</label>
                      <input
                        value={form.firstName}
                        onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                        className="input"
                        placeholder={t('cotisants.firstNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="field-label">{t('cotisants.lastName')}</label>
                      <input
                        value={form.lastName}
                        onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                        className="input"
                        placeholder={t('cotisants.lastNamePlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label">{t('cotisants.email')}</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="input"
                        placeholder="membre@exemple.fr"
                      />
                    </div>
                    <div>
                      <label className="field-label">{t('cotisants.phone')}</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        className="input"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="field-label">{t('cotisants.address')}</label>
                    <input
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                      className="input"
                      placeholder={t('cotisants.addressPlaceholder')}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="field-label">{t('cotisants.mode')}</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {MODES.map((m) => {
                    const active = form.mode === m;
                    const Icon = m === 'FULL' ? Wallet : CalendarClock;
                    const label =
                      m === 'FULL'
                        ? t('cotisants.modeFull')
                        : m === 'MONTHLY'
                          ? t('cotisants.modeMonthly')
                          : t('cotisants.modeInstallments');
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, mode: m }))}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                          active
                            ? 'border-brand bg-brand-tint text-brand-strong'
                            : 'border-line text-ink-soft hover:bg-surface-raised'
                        }`}
                      >
                        <Icon size={16} /> {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label">{t('cotisants.totalAmount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.total}
                    onChange={(e) => setForm((f) => ({ ...f, total: e.target.value }))}
                    className="input"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="field-label">{t('cotisants.startMonth')}</label>
                  <input
                    type="month"
                    value={form.startMonth}
                    onChange={(e) => setForm((f) => ({ ...f, startMonth: e.target.value }))}
                    className="input"
                  />
                </div>
              </div>

              {form.mode !== 'FULL' && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="field-label">{t('cotisants.installmentCount')}</label>
                    <input
                      type="number"
                      min="1"
                      value={form.count}
                      onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                      className="input"
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <p className="rounded-lg bg-surface-raised px-3 py-2 text-sm text-ink-muted">
                      {t('cotisants.perMonth')}{' '}
                      <span className="font-semibold text-ink">{euro(previewPer)}</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label">{t('cotisants.incomeAccount')}</label>
                  <input
                    value={form.incomeAccount}
                    onChange={(e) => setForm((f) => ({ ...f, incomeAccount: e.target.value }))}
                    className="input"
                    placeholder="756"
                  />
                </div>
                <div>
                  <label className="field-label">{t('cotisants.matchLabel')}</label>
                  <input
                    value={form.matchLabel}
                    onChange={(e) => setForm((f) => ({ ...f, matchLabel: e.target.value }))}
                    className="input"
                    placeholder={t('cotisants.matchLabelPlaceholder')}
                  />
                </div>
              </div>
              <p className="text-xs text-ink-muted">{t('cotisants.matchLabelHint')}</p>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              {formError && (
                <span className="mr-auto flex items-center gap-1.5 text-sm font-medium text-expense">
                  <AlertTriangle size={14} /> {formError}
                </span>
              )}
              <button onClick={resetForm} className="btn btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={submit} disabled={create.isPending} className="btn btn-primary">
                <Check size={16} /> {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={() => setDetailId(null)}>
          <div
            className="reveal h-full w-full max-w-md overflow-y-auto bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="eyebrow flex items-center gap-2">
                  {modeLabel(detail)}
                  {detail.source === 'AUTO' && (
                    <span className="pill pill-posted" title={t('cotisants.aiExtractedHint')}>
                      <Sparkles size={11} /> {t('cotisants.sourceAuto')}
                    </span>
                  )}
                </p>
                <h2 className="page-title mt-1 text-xl">{name(detail)}</h2>
              </div>
              <button onClick={() => setDetailId(null)} className="btn btn-ghost !px-2 !py-2">
                <X size={18} />
              </button>
            </div>

            {detail.status === 'PENDING' && (
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg bg-gold-tint p-3">
                <AlertTriangle size={16} className="shrink-0 text-gold" />
                <span className="flex-1 text-sm text-ink">{t('cotisants.pendingHint')}</span>
                <button onClick={() => validate.mutate(detail.id)} className="btn btn-primary !py-1.5">
                  <BadgeCheck size={14} /> {t('cotisants.validate')}
                </button>
              </div>
            )}

            {(detail.email || detail.phone || detail.address) && (
              <div className="mb-4 space-y-1.5 rounded-lg bg-surface-raised p-3 text-sm">
                {detail.email && (
                  <p className="flex items-center gap-2 text-ink-soft">
                    <Mail size={14} className="text-ink-muted" /> {detail.email}
                  </p>
                )}
                {detail.phone && (
                  <p className="flex items-center gap-2 text-ink-soft">
                    <Phone size={14} className="text-ink-muted" /> {detail.phone}
                  </p>
                )}
                {detail.address && (
                  <p className="flex items-center gap-2 text-ink-soft">
                    <MapPin size={14} className="shrink-0 text-ink-muted" /> {detail.address}
                  </p>
                )}
              </div>
            )}

            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-xs text-ink-muted">{t('cotisants.kpiExpected')}</p>
                <p className="mt-1 font-semibold text-ink">{euro(detail.totalExpected)}</p>
              </div>
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-xs text-ink-muted">{t('cotisants.kpiCollected')}</p>
                <p className="mt-1 font-semibold text-income">{euro(detail.collected)}</p>
              </div>
            </div>

            <p className="eyebrow mb-2">{t('cotisants.schedule')}</p>
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('cotisants.month')}</th>
                    <th className="text-right">{t('cotisants.expected')}</th>
                    <th className="text-right">{t('cotisants.paid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.installments.map((i) => {
                    const done = i.paid >= i.expected && i.expected > 0;
                    return (
                      <tr key={i.month}>
                        <td data-label={t('cotisants.month')}>
                          <span className="flex items-center gap-2">
                            {MONTH_LABEL(i.month)}
                            {i.auto && <Sparkles size={12} className="text-brand" />}
                          </span>
                        </td>
                        <td data-label={t('cotisants.expected')} className="text-right text-ink-muted">
                          {euro(i.expected)}
                        </td>
                        <td data-label={t('cotisants.paid')} className="text-right">
                          {done ? (
                            <span className="font-medium text-income">{euro(i.paid)}</span>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
